import { NO_IMAGE_PLACEHOLDER } from '../constants/images';

/**
 * Global image error handler that automatically adds error handling to all img elements
 * This should be called once when the app initializes
 */
export const initializeGlobalImageErrorHandler = () => {
  // Handle errors for existing images
  document.addEventListener('error', handleImageError, true);

  // Handle dynamically added images
  observeNewImages();
};

/**
 * Handles image loading errors
 */
function handleImageError(event: Event) {
  const target = event.target as HTMLElement;

  if (target.tagName === 'IMG') {
    const img = target as HTMLImageElement;

    // Prevent infinite loop if fallback also fails
    if (img.src === NO_IMAGE_PLACEHOLDER || img.dataset.fallbackApplied === 'true') {
      return;
    }

    // Mark that we've applied fallback to prevent loops
    img.dataset.fallbackApplied = 'true';
    img.dataset.originalSrc = img.src;

    // Set the fallback image
    img.src = NO_IMAGE_PLACEHOLDER;

    // Add a class for styling if needed
    img.classList.add('image-fallback-applied');
  }
}

/**
 * Observes DOM for new image elements and adds error handlers
 */
function observeNewImages() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;

          // Check if the added node is an img
          if (element.tagName === 'IMG') {
            addImageErrorHandler(element as HTMLImageElement);
          }

          // Check for img elements within the added node
          const images = element.querySelectorAll('img');
          images.forEach((img) => addImageErrorHandler(img as HTMLImageElement));
        }
      });
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

/**
 * Adds error handler to a specific image element
 */
function addImageErrorHandler(img: HTMLImageElement) {
  // Skip if already has fallback applied
  if (img.dataset.fallbackApplied === 'true') {
    return;
  }

  // Add error handler if image hasn't loaded yet
  if (!img.complete || img.naturalHeight === 0) {
    img.addEventListener('error', handleImageError, { once: true });
  }
}

/**
 * Utility function to reset an image to its original source
 * Useful for retry functionality
 */
export function resetImageSource(img: HTMLImageElement) {
  const originalSrc = img.dataset.originalSrc;
  if (originalSrc) {
    img.dataset.fallbackApplied = 'false';
    img.src = originalSrc;
    img.classList.remove('image-fallback-applied');
  }
}